import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { cn } from "@/lib/utils";

/** ~6 rows visible; user scrolls to see all items */
const LIST_MAX_HEIGHT = "max-h-48";

function normalizeSearch(text) {
  return (text || "").toLowerCase().trim();
}

/**
 * Searchable multi-select for GR / citations / clauses.
 * Shows all items in a scrollable list; search filters in place. Selected items pin to top.
 */
export default function ReferencePickList({
  title,
  icon: Icon,
  items = [],
  idKey,
  getLabel,
  getSecondary,
  getSearchText,
  checked = [],
  onToggle,
  searchPlaceholder = "Search…",
  testIdPrefix = "ref-pick",
}) {
  const [query, setQuery] = useState("");

  const checkedSet = useMemo(() => new Set(checked), [checked]);

  const filtered = useMemo(() => {
    const q = normalizeSearch(query);
    let list = items;
    if (q) {
      list = items.filter((it) => normalizeSearch(getSearchText(it)).includes(q));
    } else {
      // Pin selected items at top when not searching
      const selected = [];
      const rest = [];
      for (const it of items) {
        if (checkedSet.has(it[idKey])) selected.push(it);
        else rest.push(it);
      }
      list = [...selected, ...rest];
    }
    return list;
  }, [items, query, checkedSet, idKey, getSearchText]);

  const isSearching = query.trim().length > 0;

  const selectedItems = useMemo(
    () => items.filter((it) => checkedSet.has(it[idKey])),
    [items, checkedSet, idKey],
  );

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs flex items-center gap-1 mb-0">
          {Icon && <Icon className="size-3.5 shrink-0" />}
          {title}
        </Label>
        {checked.length > 0 && (
          <span
            className="text-[10px] text-muted-foreground tabular-nums"
            data-testid={`${testIdPrefix}-count`}
          >
            {checked.length} selected
          </span>
        )}
      </div>

      {selectedItems.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5"
          data-testid={`${testIdPrefix}-chips`}
        >
          {selectedItems.map((it) => (
            <span
              key={it[idKey]}
              className="inline-flex items-center gap-1 max-w-full rounded-md border border-border bg-secondary/60 px-2 py-0.5 text-[11px] leading-snug"
            >
              <span className="gujarati truncate max-w-[200px]">{getLabel(it)}</span>
              <button
                type="button"
                className="shrink-0 rounded p-0.5 hover:bg-secondary text-muted-foreground hover:text-foreground"
                aria-label={`Remove ${getLabel(it)}`}
                onClick={() => onToggle(it[idKey])}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={searchPlaceholder}
          className="h-8 pl-8 text-xs"
          data-testid={`${testIdPrefix}-search`}
        />
      </div>

      <div
        className={cn("overflow-y-auto border border-border rounded-md", LIST_MAX_HEIGHT)}
        data-testid={`${testIdPrefix}-list`}
      >
        {items.length === 0 && (
          <div className="p-2 text-xs text-muted-foreground">No items.</div>
        )}
        {items.length > 0 && filtered.length === 0 && (
          <div className="p-2 text-xs text-muted-foreground">
            No matches for &quot;{query}&quot;
          </div>
        )}
        {filtered.map((it) => {
          const id = it[idKey];
          const isChecked = checkedSet.has(id);
          return (
            <label
              key={id}
              className={cn(
                "flex items-start gap-2 text-xs border-b border-border/50 last:border-0",
                "hover:bg-secondary/50 cursor-pointer p-2",
                isChecked && "bg-secondary/30",
              )}
            >
              <Checkbox
                className="mt-0.5 shrink-0"
                checked={isChecked}
                onCheckedChange={() => onToggle(id)}
              />
              <span className="min-w-0 flex-1">
                <span className="gujarati block font-medium leading-snug line-clamp-2">
                  {getLabel(it)}
                </span>
                {getSecondary && (
                  <span className="gujarati block text-[10px] text-muted-foreground mt-0.5 line-clamp-2">
                    {getSecondary(it)}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>

      {items.length > 0 && (
        <p className="text-[10px] text-muted-foreground" data-testid={`${testIdPrefix}-hint`}>
          {isSearching ? (
            <>
              Showing {filtered.length} of {items.length} match{filtered.length === 1 ? "" : "es"}
            </>
          ) : (
            <>
              {items.length} item{items.length === 1 ? "" : "s"} — scroll to browse, or search to
              filter
            </>
          )}
        </p>
      )}
    </div>
  );
}
